/**
 * The address a customer's API key works against (E17/E18 publishing, docs/fibtec-etlgen-e18-
 * publishing.md §2). A key is for the key API, not the portal's, and its address is a CloudFormation
 * output of the account the service runs in, so without this a customer has no way to learn it: the
 * command-line client, the Python client and the Claude Code plugin all ask for it as
 * `ETLGEN_BASE_URL`. Written into the build from the stack's output, like the other `VITE_*` values.
 *
 * It is not a secret (the authorizer, and the key, are what protect the API), so it is shown
 * to whoever can open the page. Nothing is rendered when the build did not carry one.
 */

import { useState } from 'react';

export function KeyApiAddress({ address }: { address: string }) {
  const [note, setNote] = useState('');
  if (!address) return null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(address);
      setNote('Copied to the clipboard.');
    } catch {
      setNote('Could not copy automatically. Select the address and copy it yourself.');
    }
  }

  return (
    <div className="result">
      <h2>The key API&rsquo;s address</h2>
      <p className="muted">
        A key works against this address, not the portal&rsquo;s. Set it as{' '}
        <span className="mono">ETLGEN_BASE_URL</span> next to your key: the command-line client, the
        Python client and the Claude Code plugin all read it. It is not a secret; the key is what
        authorises a call.
      </p>
      <input
        className="mono"
        type="text"
        readOnly
        value={address}
        aria-label="Key API address"
        autoComplete="off"
        spellCheck={false}
        onFocus={(event) => event.currentTarget.select()}
      />
      <div className="field-inline">
        <button className="btn btn--primary" onClick={() => void copy()}>
          Copy address
        </button>
      </div>
      {note && <p className="muted small">{note}</p>}
    </div>
  );
}
